import { useCallback, useEffect, useRef, useState } from 'react'
import { getAuthHeaders } from '../utils/auth.js'
import { LIST_ACTIONS } from '../utils/shoppingListActions.js'
import {
  uniqueLookupNames,
  remapBatchResultsToLookupQueries,
} from '../utils/enrichGroceryItemsWithLookup.js'

const AISLE_DELAY_MS = 1800
const DEBOUNCE_MS = 300

/**
 * Single place for store-dependent metadata refresh: saved list, ad-hoc list batch lookup,
 * delayed aisle refresh after list append, and pending inline lookup results.
 * Uses generation guards so stale responses never apply after store changes.
 */
export function useShopRefreshOrchestrator({
  selectedStore,
  loadedSavedListId,
  groceryListItems,
  dispatchListAction,
  pendingLookupResults,
  setPendingLookupResults,
  setPendingLookupNoMatches,
}) {
  const [shopRefreshError, setShopRefreshError] = useState(null)

  const refreshDebounceRef = useRef(null)
  const aisleTimerRef = useRef(null)
  const metadataInFlightRef = useRef(false)
  const metadataGenerationRef = useRef(0)
  const uploadGenerationRef = useRef(0)

  const selectedStoreIdRef = useRef(selectedStore?.id)
  selectedStoreIdRef.current = selectedStore?.id

  const selectedStoreRef = useRef(selectedStore)
  selectedStoreRef.current = selectedStore

  const loadedSavedListIdRef = useRef(loadedSavedListId)
  loadedSavedListIdRef.current = loadedSavedListId

  const groceryListItemsRef = useRef(groceryListItems)
  groceryListItemsRef.current = groceryListItems

  const pendingLookupResultsRef = useRef(pendingLookupResults)
  pendingLookupResultsRef.current = pendingLookupResults

  /** Last list id used for delayed aisle refresh (for retry). */
  const lastDelayedAisleListIdRef = useRef(null)
  const lastFailedKindRef = useRef(null) // 'metadata' | 'aisle' | 'upload'

  const clearShopRefreshError = useCallback(() => {
    setShopRefreshError(null)
    lastFailedKindRef.current = null
  }, [])

  const runDelayedAisleRefreshCore = useCallback(async () => {
    const listId = lastDelayedAisleListIdRef.current
    if (!listId) return
    const storeId = selectedStoreIdRef.current
    if (!storeId) {
      lastFailedKindRef.current = 'aisle'
      setShopRefreshError(
        'Store was not ready when refreshing aisle info. Choose a store, then retry.'
      )
      return
    }
    const refreshParams = new URLSearchParams()
    refreshParams.set('refresh', 'true')
    refreshParams.set('store', storeId)
    try {
      const res = await fetch(`/saved-lists/${listId}?${refreshParams}`, { headers: getAuthHeaders() })
      if (!res.ok) {
        lastFailedKindRef.current = 'aisle'
        setShopRefreshError(`Could not refresh aisle layout (${res.status}).`)
        return
      }
      const refreshed = await res.json()
      if (refreshed?.items?.length) {
        dispatchListAction({ type: LIST_ACTIONS.REFRESH_METADATA, refreshedItems: refreshed.items })
      }
      setShopRefreshError(null)
      lastFailedKindRef.current = null
    } catch (err) {
      lastFailedKindRef.current = 'aisle'
      setShopRefreshError(err?.message || 'Aisle refresh failed.')
    }
  }, [dispatchListAction])

  const scheduleDelayedAisleRefresh = useCallback(
    (listId) => {
      if (!listId) return
      lastDelayedAisleListIdRef.current = listId
      if (aisleTimerRef.current) {
        clearTimeout(aisleTimerRef.current)
        aisleTimerRef.current = null
      }
      aisleTimerRef.current = setTimeout(() => {
        aisleTimerRef.current = null
        void runDelayedAisleRefreshCore()
      }, AISLE_DELAY_MS)
    },
    [runDelayedAisleRefreshCore]
  )

  const performMetadataRefresh = useCallback(async () => {
    const sid = selectedStoreIdRef.current
    if (!sid) return
    if (metadataInFlightRef.current) return

    const gen = ++metadataGenerationRef.current
    metadataInFlightRef.current = true
    lastFailedKindRef.current = 'metadata'

    try {
      const listId = loadedSavedListIdRef.current
      if (listId) {
        const params = new URLSearchParams()
        params.set('refresh', 'true')
        params.set('store', sid)
        const response = await fetch(`/saved-lists/${listId}?${params}`, { headers: getAuthHeaders() })
        if (metadataGenerationRef.current !== gen) return
        if (!response.ok) {
          setShopRefreshError(`Could not refresh list for this store (${response.status}).`)
          return
        }
        const list = await response.json()
        if (list?.items) {
          dispatchListAction({ type: LIST_ACTIONS.REFRESH_METADATA, refreshedItems: list.items })
        }
      } else {
        const items = groceryListItemsRef.current
        if (!items?.length) {
          setShopRefreshError(null)
          lastFailedKindRef.current = null
          return
        }
        const itemNames = uniqueLookupNames(items)
        if (itemNames.length === 0) {
          setShopRefreshError(null)
          lastFailedKindRef.current = null
          return
        }
        const params = new URLSearchParams()
        params.set('store', sid)
        const response = await fetch(`/grocery/lookup/batch?${params}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: itemNames }),
        })
        if (metadataGenerationRef.current !== gen) return
        if (!response.ok) {
          setShopRefreshError(`Could not refresh items for this store (${response.status}).`)
          return
        }
        const data = await response.json()
        dispatchListAction({
          type: LIST_ACTIONS.REFRESH_METADATA,
          refreshedItems: remapBatchResultsToLookupQueries(itemNames, data?.results),
        })
      }
      if (metadataGenerationRef.current === gen) {
        setShopRefreshError(null)
        lastFailedKindRef.current = null
      }
    } catch (err) {
      if (metadataGenerationRef.current === gen) {
        setShopRefreshError(err?.message || 'Refresh failed.')
      }
    } finally {
      metadataInFlightRef.current = false
    }
  }, [dispatchListAction])

  // Debounced metadata refresh when store or loaded saved list identity changes.
  useEffect(() => {
    if (refreshDebounceRef.current) {
      clearTimeout(refreshDebounceRef.current)
      refreshDebounceRef.current = null
    }

    if (!selectedStore?.id) {
      metadataGenerationRef.current += 1
      uploadGenerationRef.current += 1
      return
    }

    refreshDebounceRef.current = setTimeout(() => {
      refreshDebounceRef.current = null
      void performMetadataRefresh()
    }, DEBOUNCE_MS)

    return () => {
      if (refreshDebounceRef.current) {
        clearTimeout(refreshDebounceRef.current)
        refreshDebounceRef.current = null
      }
    }
  }, [selectedStore?.id, loadedSavedListId, performMetadataRefresh])

  // Refresh pending inline lookup results when store changes (not when result content changes).
  useEffect(() => {
    async function refreshSearchResults() {
      const results = pendingLookupResultsRef.current
      const sid = selectedStoreIdRef.current
      if (!results?.length || !sid) return

      const gen = ++uploadGenerationRef.current
      lastFailedKindRef.current = 'upload'

      const itemNames = []
      results.forEach((aisle) => {
        aisle.items.forEach((item) => {
          if (item.item && !itemNames.includes(item.item)) {
            itemNames.push(item.item)
          }
        })
      })
      if (itemNames.length === 0) return

      try {
        const params = new URLSearchParams()
        params.set('store', sid)
        const response = await fetch(`/grocery/lookup/batch?${params}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: itemNames }),
        })
        if (uploadGenerationRef.current !== gen) return
        if (!response.ok) {
          setShopRefreshError(`Could not refresh search results for this store (${response.status}).`)
          return
        }
        const data = await response.json()
        if (uploadGenerationRef.current !== gen) return

        const successfulMatches = data.results.filter(
          (result) =>
            result.aisle_id !== 'Unknown' &&
            result.aisle_id !== 'Error' &&
            result.confidence >= 0.6
        )
        const failedMatches = data.results.filter(
          (result) =>
            result.aisle_id === 'Unknown' ||
            result.aisle_id === 'Error' ||
            result.confidence < 0.6
        )

        const groupedByAisle = successfulMatches.reduce((acc, result) => {
          const aisleKey = `${result.aisle_id}-${result.aisle_name}`
          if (!acc[aisleKey]) {
            acc[aisleKey] = {
              aisle_id: result.aisle_id,
              aisle_name: result.aisle_name,
              items: [],
            }
          }
          acc[aisleKey].items.push(result)
          return acc
        }, {})

        const sortedAisles = Object.values(groupedByAisle).sort((a, b) => {
          const aNum = parseInt(String(a.aisle_id).replace(/\D/g, ''), 10) || 999
          const bNum = parseInt(String(b.aisle_id).replace(/\D/g, ''), 10) || 999
          return aNum - bNum
        })

        setPendingLookupResults(sortedAisles)
        setPendingLookupNoMatches(failedMatches)
        setShopRefreshError(null)
        lastFailedKindRef.current = null
      } catch (err) {
        if (uploadGenerationRef.current === gen) {
          setShopRefreshError(err?.message || 'Could not refresh search results.')
        }
      }
    }

    if (pendingLookupResultsRef.current?.length > 0 && selectedStore?.id) {
      void refreshSearchResults()
    }
  }, [selectedStore?.id, setPendingLookupResults, setPendingLookupNoMatches])

  const retryShopRefresh = useCallback(() => {
    const kind = lastFailedKindRef.current
    setShopRefreshError(null)
    lastFailedKindRef.current = null
    if (kind === 'aisle' && lastDelayedAisleListIdRef.current) {
      void runDelayedAisleRefreshCore()
      return
    }
    if (kind === 'upload' && pendingLookupResultsRef.current?.length && selectedStoreIdRef.current) {
      // Re-run upload path by bumping effect: simplest is invoke same logic
      const sid = selectedStoreIdRef.current
      const results = pendingLookupResultsRef.current
      const gen = ++uploadGenerationRef.current
      ;(async () => {
        const itemNames = []
        results.forEach((aisle) => {
          aisle.items.forEach((item) => {
            if (item.item && !itemNames.includes(item.item)) {
              itemNames.push(item.item)
            }
          })
        })
        if (!itemNames.length) return
        try {
          const params = new URLSearchParams()
          params.set('store', sid)
          const response = await fetch(`/grocery/lookup/batch?${params}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: itemNames }),
          })
          if (uploadGenerationRef.current !== gen) return
          if (!response.ok) {
            setShopRefreshError(`Could not refresh search results (${response.status}).`)
            lastFailedKindRef.current = 'upload'
            return
          }
          const data = await response.json()
          const successfulMatches = data.results.filter(
            (r) => r.aisle_id !== 'Unknown' && r.aisle_id !== 'Error' && r.confidence >= 0.6
          )
          const failedMatches = data.results.filter(
            (r) => r.aisle_id === 'Unknown' || r.aisle_id === 'Error' || r.confidence < 0.6
          )
          const groupedByAisle = successfulMatches.reduce((acc, result) => {
            const aisleKey = `${result.aisle_id}-${result.aisle_name}`
            if (!acc[aisleKey]) {
              acc[aisleKey] = { aisle_id: result.aisle_id, aisle_name: result.aisle_name, items: [] }
            }
            acc[aisleKey].items.push(result)
            return acc
          }, {})
          const sortedAisles = Object.values(groupedByAisle).sort((a, b) => {
            const aNum = parseInt(String(a.aisle_id).replace(/\D/g, ''), 10) || 999
            const bNum = parseInt(String(b.aisle_id).replace(/\D/g, ''), 10) || 999
            return aNum - bNum
          })
          setPendingLookupResults(sortedAisles)
          setPendingLookupNoMatches(failedMatches)
        } catch (e) {
          if (uploadGenerationRef.current === gen) {
            setShopRefreshError(e?.message || 'Retry failed.')
            lastFailedKindRef.current = 'upload'
          }
        }
      })()
      return
    }
    void performMetadataRefresh()
  }, [performMetadataRefresh, runDelayedAisleRefreshCore, setPendingLookupNoMatches, setPendingLookupResults])

  useEffect(() => {
    return () => {
      if (aisleTimerRef.current) {
        clearTimeout(aisleTimerRef.current)
        aisleTimerRef.current = null
      }
      if (refreshDebounceRef.current) {
        clearTimeout(refreshDebounceRef.current)
        refreshDebounceRef.current = null
      }
    }
  }, [])

  return {
    shopRefreshError,
    clearShopRefreshError,
    retryShopRefresh,
    scheduleDelayedAisleRefresh,
  }
}
